import {
  booleanAttribute,
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  Renderer2
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';

const ISODATE_REGEX =
  /^(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))$/;
const DATE_REGEX = /^\d{2}\.\d{2}\.\d{4}$/;

export function isValidIsoDateFormat(isoDate: string): boolean {
  return ISODATE_REGEX.test(isoDate);
}

export function isValidDateString(date: string): boolean {
  return DATE_REGEX.test(date);
}

/**
 * Validate Date
 *
 * @example
 * // returns true
 * isValidISODate('2013-12-02');
 * @example
 * // returns false
 * isValidISODate('2013-13-40');
 *
 * @param isoDate
 * @return valid
 */
export function isValidISODate(isoDate: string): boolean {
  let result = true;
  if (isoDate) {
    if (!isValidIsoDateFormat(isoDate)) {
      result = false;
    } else {
      const jsDate: Date = new Date(isoDate);
      if (jsDate.toISOString().substr(0, 10) !== isoDate) {
        result = false;
      }
    }
  }
  // console.log('isValidISODate', isoDate, result );
  return result;
}

export const INPUT_DATE_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DateFieldDirective),
  multi: true
};

export const INPUT_DATE_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => DateFieldDirective),
  multi: true
};

/**
 * The accessor for writing a value and listening to changes on a date input element
 *
 *  ### Example
 *   `<input mngDateField type="text" [formControl]="modelItem.control">`
 **/
@Directive({
  selector: '[mngDateField]',

  providers: [INPUT_DATE_VALUE_ACCESSOR, INPUT_DATE_VALIDATOR]
})
export class DateFieldDirective implements ControlValueAccessor, Validator {
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);

  viewValue: string;

  // Flag für die Validierung der Eingabe -> wenn false wird der eingegebene bei einem Eingabefehler so wie er ist
  // in das Model geschrieben und die Validierung muss anderswo passieren
  dateValidationRequired = input(true, { transform: booleanAttribute });

  allowEmpty = input(true, { transform: booleanAttribute });

  // mngDateField wird verwendet um eine Date Instanz vom eingegeben Wert zu erhalten
  @HostListener('change', ['$event.target.value'])
  onChange = (value: any) => {
    return;
  };

  @HostListener('input', ['$event.target.value'])
  onInput = this.onChange;

  @HostListener('blur', [])
  onTouched = () => {
    return;
  };

  /**
   * Wert --> HTML Element
   * @param value
   */
  writeValue(value: string) {
    // MODEL -> VIEW
    const viewValue = fromIsoDateString(value);
    this.viewValue = viewValue;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', viewValue);
  }

  /**
   *
   * @param fn
   */
  registerOnChange(fn: (_: any) => void) {
    // VIEW -> MODEL
    this.onChange = (value) => {
      this.viewValue = value;

      if (!this.dateValidationRequired()) {
        const valid: boolean = this.isValidDateString(value);
        if (!valid) {
          // Invaliden Wert so wie er ist in das Model zurückschreiben
          // -> Die Validierung muss anderswo passieren
          fn(value);
          return;
        }
      }

      const isoDate = value === '' ? null : toIsoDateString(value);
      fn(isoDate);
    };
    this.onInput = this.onChange;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  isValidDateString(value: string): boolean {
    let valid: boolean = isValidDateString(value);

    if (valid) {
      valid = isValidISODate(toIsoDateString(this.viewValue));
    }

    return valid;
  }

  isExisting(value: string): boolean {
    if (this.allowEmpty()) {
      return true;
    } else {
      return !!value;
    }
  }

  validate(c: AbstractControl): { [key: string]: any } {
    if (!this.dateValidationRequired()) {
      return null;
    } else {
      const validFormat = this.isValidDateString(this.viewValue);
      const validRequired = this.isExisting(this.viewValue);
      if (!validRequired) {
        return { 'Eine Eingabe ist erforderlich': true };
      } else if (validRequired && !validFormat) {
        return { 'Datum bitte im Format TT.MM.JJJJ eingeben': true };
      } else {
        return null;
      }
    }
  }
}

function fromIsoDateString(value: string): string {
  if (value && value.length === 10) {
    const parts = value.split('-');
    const result = `${parts[2]}.${parts[1]}.${parts[0]}`;
    return result;
  } else {
    return null;
  }
}

function toIsoDateString(value: string): string {
  if (value && value.length === 10) {
    const parts = value.split('.');
    const result = `${parts[2]}-${parts[1]}-${parts[0]}`;
    return result;
  } else {
    return null;
  }
}
